import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import {
  IProfileMultiSelectState,
  ProfileMultiSelectFilterService,
} from '@shared/components/profile-general-filters/profile-multi-select-filter.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { map, startWith } from 'rxjs';
import { ICampaignDetailsDeliverablesFilterOption } from '../campaign-details-deliverables.interface';

@Component({
  imports: [
    IconComponent,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    NgxSkeletonLoaderModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-campaign-details-deliverables-filter-select',
  templateUrl: './campaign-details-deliverables-filter-select.component.html',
})
export class CampaignDetailsDeliverablesFilterSelectComponent {
  private readonly multiSelectFilterService = inject(ProfileMultiSelectFilterService);
  private readonly translate = inject(TranslateService);

  public readonly emptyLabel = input.required<string>();
  public readonly loading = input<boolean>(false);
  public readonly options = input<ICampaignDetailsDeliverablesFilterOption<string>[]>([]);
  public readonly searchPlaceholder = input<string>('');
  public readonly selectedValues = input<Iterable<string>>(new Set<string>());
  public readonly exclusiveOption = input<string>('');
  public readonly exclusiveOptionActive = input<boolean>(false);
  public readonly showBulkActions = input<boolean>(true);
  public readonly showSearch = input<boolean>(true);

  public readonly exclusiveOptionToggled = output<void>();
  public readonly selectionChanged = output<Set<string>>();
  /**
   * Fires once when the panel closes, but only if the selection actually changed while it was
   * open - purely opt-in (nobody has to listen), so existing consumers that only care about the
   * instant `selectionChanged` stream are completely unaffected. Lets a consumer defer expensive
   * work (e.g. a network refetch) until the user is done picking options instead of on every click,
   * matching the pattern already used by `ProfileGeneralFiltersComponent`.
   */
  public readonly panelClosed = output<void>();
  public readonly selectionState = signal<IProfileMultiSelectState<string>>(
    this.multiSelectFilterService.createUnfilteredSelectionState<string>(),
  );
  public readonly selectionControl = new FormControl<string[]>([], { nonNullable: true });
  public readonly searchControl = new FormControl<string>('', { nonNullable: true });
  public readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(startWith('')), { initialValue: '' });
  private panelSelectionSnapshot: string | null = null;
  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly displayedOptions = computed((): ICampaignDetailsDeliverablesFilterOption<string>[] => {
    const query = this.searchQuery().trim().toLowerCase();
    const lang = this.languageChange();
    const sorted = [...this.options()].sort((a, b) => a.label.localeCompare(b.label, lang));

    if (!query) {
      return sorted;
    }

    return sorted.filter((option) => option.label.toLowerCase().includes(query));
  });

  public readonly hiddenSelectedOptions = computed((): ICampaignDetailsDeliverablesFilterOption<string>[] => {
    if (!this.searchQuery().trim()) {
      return [];
    }

    const displayedValues = new Set(this.displayedOptions().map((opt) => opt.value));

    return this.selectionState()
      .uiSelectedValues.filter((v) => !displayedValues.has(v))
      .map((v) => this.options().find((opt) => opt.value === v))
      .filter((opt): opt is ICampaignDetailsDeliverablesFilterOption<string> => opt !== undefined);
  });

  public readonly triggerLabel = computed((): string => {
    this.languageChange();

    if (this.exclusiveOptionActive()) {
      return this.exclusiveOption();
    }

    const selectedLabels = this.options()
      .filter((option) => this.selectionState().uiSelectedValues.includes(option.value))
      .map((option) => option.label);

    if (selectedLabels.length === 0) {
      return this.emptyLabel();
    }

    return this.multiSelectFilterService.buildSelectionSummary(
      selectedLabels[0],
      selectedLabels.length,
      (this.translate.instant('shared.more') as string).toLowerCase(),
    );
  });

  constructor() {
    effect(() => {
      const nextState = this.multiSelectFilterService.syncSelectionState(
        this.options().map((option) => option.value),
        new Set(this.selectedValues()),
        this.selectionState(),
      );

      if (!this.selectionStatesAreEqual(this.selectionState(), nextState)) {
        this.selectionState.set(nextState);
      }

      const controlValues = this.exclusiveOptionActive()
        ? ['__exclusive__', ...nextState.uiSelectedValues]
        : nextState.uiSelectedValues;

      if (!this.selectionValuesAreEqual(this.selectionControl.value, controlValues)) {
        this.selectionControl.setValue(controlValues, { emitEvent: false });
      }
    });
  }

  public onSelectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(
      this.multiSelectFilterService.createExplicitSelectionState(this.options().map((option) => option.value)),
    );
    this.selectionControl.setValue(
      this.options().map((option) => option.value),
      { emitEvent: false },
    );
    this.emitSelection();
  }

  public onSelectionChange(event: MatSelectChange<string[]>): void {
    const visibleSelectedValues: string[] = (event.value ?? []).filter((value) => value !== '__exclusive__');
    const query = this.searchQuery().trim().toLowerCase();
    const hiddenSelected: string[] = query
      ? this.selectionState().uiSelectedValues.filter((v) => !this.displayedOptions().some((opt) => opt.value === v))
      : [];

    const nextSelectedValues: string[] = [...new Set([...visibleSelectedValues, ...hiddenSelected])];

    if (
      this.exclusiveOption() &&
      this.selectionValuesAreEqual(nextSelectedValues, this.selectionState().uiSelectedValues)
    ) {
      return;
    }

    const nextState =
      nextSelectedValues.length === 0
        ? this.multiSelectFilterService.createUnfilteredSelectionState<string>()
        : this.multiSelectFilterService.createExplicitSelectionState(nextSelectedValues);

    this.selectionState.set(nextState);
    this.emitSelection();
  }

  public onUnselectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(this.multiSelectFilterService.createUnfilteredSelectionState<string>());
    this.selectionControl.setValue([], { emitEvent: false });
    this.emitSelection();
  }

  public onOpenedChange(opened: boolean): void {
    if (opened) {
      this.panelSelectionSnapshot = this.getSelectionSnapshot();
    }
  }

  public onPanelClosed(): void {
    const nextSelectionSnapshot = this.getSelectionSnapshot();
    const selectionChanged =
      this.panelSelectionSnapshot !== null && this.panelSelectionSnapshot !== nextSelectionSnapshot;

    this.panelSelectionSnapshot = null;

    if (!selectionChanged) {
      return;
    }

    this.panelClosed.emit();
  }

  private getSelectionSnapshot(): string {
    return JSON.stringify(
      Array.from(
        this.multiSelectFilterService.normalizeValuesForEmission(
          this.selectionState(),
          this.options().map((option) => option.value),
        ),
      ).sort(),
    );
  }

  private emitSelection(): void {
    this.selectionChanged.emit(
      this.multiSelectFilterService.normalizeValuesForEmission(
        this.selectionState(),
        this.options().map((option) => option.value),
      ),
    );
  }

  private selectionStatesAreEqual(
    left: IProfileMultiSelectState<string>,
    right: IProfileMultiSelectState<string>,
  ): boolean {
    return (
      left.presentation === right.presentation &&
      left.uiSelectedValues.length === right.uiSelectedValues.length &&
      left.uiSelectedValues.every((value, index) => value === right.uiSelectedValues[index])
    );
  }

  private selectionValuesAreEqual(left: string[], right: string[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }
}
